import ExcelJS from 'exceljs'
import { download, generateCsv, mkConfig } from 'export-to-csv'
import { create } from 'zustand'
import { combine } from 'zustand/middleware'

import { createFileName, fetchAndPrepareSessionsForExport } from '@/features/admin/sessions/utils'
import { sendPasswords } from '@/features/auth/api'
import authStore from '@/features/auth/stores/auth/auth.store'
import type { V2AccountDataType } from '@/features/auth/types'
import { PermissionsGroupsEnumType, RoleEnumTypeV2 } from '@/features/auth/types'
import logout from '@/features/auth/utils/logout'
import type { V2GroupType } from '@/features/groups/types'
import { getMeetingLink } from '@/features/meetings/utils'
import { switchUserToPasswordLoginMutation, switchUserToSsoLoginMutation } from '@/features/programs/api'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import type { V2ProgramType } from '@/features/programs/types'
import { V1UpdateSessionStatusMutation } from '@/features/sessions/api'
import { SessionStatusEnumType, type V2SessionType } from '@/features/sessions/types'
import { WHITE_LABEL_QUERY_KEY } from '@/features/ui/api/global-data'
import {
  resetMoreActionsStoreAction,
  setAttachedDataAction,
} from '@/features/ui/components/more-actions-menu/store/more-actions.actions'
import type {
  AddToCalendarModalType,
  AdminGroupStructureModalType,
  EditProfileModalType,
  EditSessionModalType,
  GroupSettingsModalType,
  SessionDetailsModalType,
  UserProfileModalType,
} from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import { deleteUserFromProgram } from '@/features/users/api'
import type { V2UserAdminDataType, V2UserDataType } from '@/features/users/types'
import type { I18nTType, TranslationKeysType } from '@/lib/i18n'
import queryClient from '@/lib/vue-query'
import subscribeToStore from '@/lib/zustand'
import router from '@/router'
import type { ToastType } from '@/types'
import type { V1WhiteLabelDataType } from '@/types/global-data'
import { ROUTES } from '@/utils/config/constants'
import { invalidateAdminUsers, invalidateSession, invalidateSessions } from '@/utils/lib/vue-query'

/**
 * KC-635 : un réglage de connexion de la plateforme, lu dans le cache
 * white-label déjà chargé par l'app. Inconnu (cache vide) : undefined, et
 * l'API tranche de toute façon.
 */
function getPlatformSetting(name: 'SAML_LOGIN_ENABLED' | 'EMAIL_LOGIN_ENABLED'): string | undefined {
  const entries = queryClient.getQueriesData<V1WhiteLabelDataType>({ queryKey: [WHITE_LABEL_QUERY_KEY] })
  for (const [, data] of entries) {
    const value = data?.setting?.find((item) => item.name === name)?.value
    if (value !== undefined) return value
  }
  return undefined
}

/** Super admin = ADMIN + SUPPORT sur le programme courant (même règle que l'impersonation). */
function isSuperAdminContext(context?: { programRoles?: RoleEnumTypeV2[] | null }): boolean {
  const roles = context?.programRoles || []
  return roles.includes(RoleEnumTypeV2.ADMIN) && roles.includes(RoleEnumTypeV2.SUPPORT)
}

// Define a generic action type
export type ActionType = {
  fn: ({
    attachedData,
    toast,
    t,
  }: {
    attachedData: MoreActionsStoreType['attachedData']
    toast: ToastType
    t: I18nTType
  }) => void | Promise<void>
  label: TranslationKeysType
  icon:
    | 'logout'
    | 'settings'
    | 'show'
    | 'copy'
    | 'edit'
    | 'cancel'
    | 'conversation'
    | 'download'
    | 'add-file'
    | 'email'
    | 'change'
    | 'calendar'
    | 'user'
  isDanger?: boolean
  /** Demande confirmation (modale) sans afficher l'action en rouge. */
  requiresConfirm?: boolean
  /** Ligne d'information grise affichée au-dessus du libellé (ex. le mode de connexion). */
  hint?: (attachedData: MoreActionsStoreType['attachedData']) => TranslationKeysType | undefined
  confirmLabel?: string
  confirmContent?: string
  restrictions?: {
    groupRoles?: RoleEnumTypeV2[]
    programRoles?: RoleEnumTypeV2[]
    sessionsStatus?: SessionStatusEnumType[]
    menteeCanBookSession?: boolean
    /**
     * Le composant appelle `custom` avec DEUX arguments (cf.
     * more-actions-menu.vue) : la donnée attachée et le contexte de rôles. Le
     * second manquait à cette signature, ce que le typecheck ne voyait pas —
     * les types Cypress fuitaient dans le typecheck de src/ et neutralisaient
     * l'inférence ici.
     */
    custom?: (
      attachedData: MoreActionsStoreType['attachedData'] | undefined,
      context?: { programRoles?: RoleEnumTypeV2[]; groupRoles?: RoleEnumTypeV2[] },
    ) => boolean
  }
}
// Define a generic CommonMoreActionsType
type CommonMoreActionsType<T extends string, P> = {
  name: T
  actions: ActionType[]
  attachedData: Partial<P>
}

// Use specific action types for different contexts
type AccountMoreActionsType = CommonMoreActionsType<
  'account',
  { account: V2AccountDataType; programRoles: RoleEnumTypeV2[] }
>
type SessionCardMoreActionsType = CommonMoreActionsType<'session-card', V2SessionType>
type UserCardMoreActionsType = CommonMoreActionsType<'user-card', V2UserDataType>
export type AdminSessionsExportMoreActionsType = CommonMoreActionsType<
  'admin-sessions-export',
  { program: V2ProgramType; periodId: string; isLoading: boolean }
>
type AdminUsersImportMoreActionsType = CommonMoreActionsType<'admin-users-import', object>

type UserRowMoreActionsType = CommonMoreActionsType<'user-row', { user: V2UserDataType; program: V2ProgramType }>

type AdminGroupActionsType = CommonMoreActionsType<'admin-group', V2GroupType>

export type MoreActionsStoreType =
  | AccountMoreActionsType
  | SessionCardMoreActionsType
  | UserCardMoreActionsType
  | AdminSessionsExportMoreActionsType
  | AdminUsersImportMoreActionsType
  | UserRowMoreActionsType
  | AdminGroupActionsType

const accountActionItem = {
  name: 'account',
  actions: [
    {
      fn: function () {
        router.push(ROUTES.PROFILE).catch(() => {})
      },
      label: 'moreActions.account.seeProfile',
      icon: 'show',
    },
    {
      fn: () => {
        // set the current loaded role to admin
        authStore.setUserActivePermissionGroup(PermissionsGroupsEnumType.ADMIN)
        // we use window.location.pathname for a fresh full reload of the page and state
        window.location.pathname = ROUTES.REDIRECT
      },
      label: 'moreActions.account.switchPlatform.admin',
      icon: 'change',
      restrictions: {
        custom: (attachedData) => {
          // active role should be user (currently seen UI) + user should have admin or manager role
          const programRoles = (attachedData as AccountMoreActionsType['attachedData']).programRoles
          const userActivePermissionGroup = authStore.userActivePermissionGroup
          const hasAccess =
            (programRoles?.includes(RoleEnumTypeV2.ADMIN) || programRoles?.includes(RoleEnumTypeV2.MANAGER)) ?? false

          return hasAccess && userActivePermissionGroup === PermissionsGroupsEnumType.USER
        },
      },
    },
    {
      fn: () => {
        // set the current loaded role to user
        authStore.setUserActivePermissionGroup(PermissionsGroupsEnumType.USER)
        // we use window.location.pathname for a fresh full reload of the page and state
        window.location.pathname = ROUTES.REDIRECT
      },
      label: 'moreActions.account.switchPlatform.user',
      icon: 'change',
      restrictions: {
        custom: (attachedData) => {
          // active role should be admin (currently seen UI) + user should have mentor or mentee role
          const programRoles = (attachedData as AccountMoreActionsType['attachedData']).programRoles
          const userActivePermissionGroup = authStore.userActivePermissionGroup
          const hasAccess =
            (programRoles?.includes(RoleEnumTypeV2.MENTOR) || programRoles?.includes(RoleEnumTypeV2.MENTEE)) ?? false
          return hasAccess && userActivePermissionGroup === PermissionsGroupsEnumType.ADMIN
        },
      },
    },
    {
      fn: function () {
        // set attached data for confirm action modal
        return logout()
      },
      label: 'moreActions.account.logout',
      confirmLabel: 'moreActions.account.logout.confirmLabel',
      icon: 'logout',
      isDanger: true,
    },
  ],
  attachedData: {},
} satisfies AccountMoreActionsType

const sessionCardActionItem = {
  name: 'session-card',
  actions: [
    {
      fn: ({ attachedData }) => {
        const session = attachedData as SessionCardMoreActionsType['attachedData']

        modalsStore.toggleModal('session-details', true)
        modalsStore.updateModalAttachedData<SessionDetailsModalType>('session-details', { sessionId: session.id })
      },
      label: 'moreActions.session.seeDetails',
      icon: 'show',
    },
    {
      fn: ({ attachedData }) => {
        const session = attachedData as SessionCardMoreActionsType['attachedData']

        modalsStore.toggleModal('edit-session', true)
        modalsStore.updateModalAttachedData<EditSessionModalType>('edit-session', { sessionId: session.id })
      },
      label: 'moreActions.session.modify',
      icon: 'edit',
      restrictions: {
        groupRoles: [RoleEnumTypeV2.MENTOR, RoleEnumTypeV2.MENTEE],
        sessionsStatus: [SessionStatusEnumType.PLANNED, SessionStatusEnumType.WAIT_CONFIRMATION],
        menteeCanBookSession: true,
      },
    },
    {
      fn: ({ attachedData, toast, t }) => {
        const session = attachedData as SessionCardMoreActionsType['attachedData']
        if (!session.id) return

        navigator.clipboard.writeText(getMeetingLink(session as V2SessionType))
        toast.success(t('moreActions.session.meetingLinkCopy.success'))
      },
      label: 'moreActions.session.meetingLinkCopy',
      icon: 'copy',
      restrictions: {
        groupRoles: [RoleEnumTypeV2.MENTOR, RoleEnumTypeV2.MENTEE],
      },
    },
    {
      fn: ({ attachedData }) => {
        const session = attachedData as SessionCardMoreActionsType['attachedData']

        modalsStore.toggleModal('add-to-calendar', true)
        modalsStore.updateModalAttachedData<AddToCalendarModalType>('add-to-calendar', {
          session: session as V2SessionType,
        })
      },
      label: 'session.addToCalendar.session.title',
      icon: 'calendar',
      restrictions: {
        groupRoles: [RoleEnumTypeV2.MENTOR, RoleEnumTypeV2.MENTEE],
      },
    },
    {
      fn: async ({ attachedData, toast, t }) => {
        const session = attachedData as SessionCardMoreActionsType['attachedData']

        await V1UpdateSessionStatusMutation({
          sessionId: session.id!,
          status: SessionStatusEnumType.CANCELLED,
        })

        await invalidateSessions()
        await invalidateSession(session.id!)
        toast.success(t('moreActions.session.cancel.success'))
      },

      label: 'moreActions.session.cancel',
      icon: 'cancel',
      isDanger: true,
      confirmLabel: 'moreActions.session.cancel.confirmLabel',
      confirmContent: `ui.modal.confirmation.irreversible`,
      restrictions: {
        groupRoles: [RoleEnumTypeV2.MENTOR],
        sessionsStatus: [
          SessionStatusEnumType.PLANNED,
          SessionStatusEnumType.WAIT_CONFIRMATION,
          SessionStatusEnumType.WAIT_REVIEW,
        ],
      },
    },
  ],
  attachedData: {},
} satisfies SessionCardMoreActionsType

const userCardActionItem = {
  name: 'user-card',
  actions: [
    {
      fn: ({ attachedData }) => {
        const user = attachedData as UserCardMoreActionsType['attachedData']
        modalsStore.toggleModal('user-profile', true)
        modalsStore.updateModalAttachedData<UserProfileModalType>('user-profile', { userId: user.id! })
      },
      label: 'moreActions.user.seeProfile',
      icon: 'show',
    },
    {
      fn: async ({ attachedData }) => {
        const user = attachedData as UserCardMoreActionsType['attachedData']
        const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
        if (!selectedProgramIdRef.value) return
        router.push(`${ROUTES.CHAT()}?sendTo=${user.id}&programId=${selectedProgramIdRef.value}`).catch(() => {})
      },
      label: 'moreActions.user.sendMessage',
      icon: 'conversation',
      restrictions: {
        groupRoles: [RoleEnumTypeV2.MENTOR, RoleEnumTypeV2.MENTEE],
      },
    },
  ],
  attachedData: {},
} satisfies UserCardMoreActionsType

const adminSessionExportActionItem = {
  name: 'admin-sessions-export',
  actions: [
    {
      fn: async ({ attachedData, toast, t }) => {
        const { program, periodId } = attachedData as AdminSessionsExportMoreActionsType['attachedData']

        try {
          moreActionsStore.setAttachedData('admin-sessions-export', { isLoading: true })
          const filename = createFileName({
            programName: program?.name,
          })
          const csvConfig = mkConfig({
            useKeysAsHeaders: true,
            filename,
          })
          const sessions = await fetchAndPrepareSessionsForExport({
            programId: program?.id,
            periodId,
          })
          const csv = generateCsv(csvConfig)(sessions)
          download(csvConfig)(csv)
        } catch (error) {
          console.error('Error exporting sessions', error)
          toast?.error(t('moreActions.admin.sessions.export.error'))
        } finally {
          moreActionsStore.setAttachedData('admin-sessions-export', { isLoading: false })
        }
      },
      label: 'moreActions.admin.sessions.CSVExport',
      icon: 'download',
      restrictions: {
        programRoles: [RoleEnumTypeV2.ADMIN, RoleEnumTypeV2.MANAGER],
      },
    },
    {
      fn: async ({ attachedData, toast, t }) => {
        const { program, periodId } = attachedData as AdminSessionsExportMoreActionsType['attachedData']

        try {
          moreActionsStore.setAttachedData('admin-sessions-export', { isLoading: true })

          const filename = createFileName({
            programName: program?.name,
          })
          const sessions = await fetchAndPrepareSessionsForExport({
            programId: program?.id,
            periodId,
          })

          /* create workbook and worksheet with ExcelJS */
          const workbook = new ExcelJS.Workbook()
          const worksheet = workbook.addWorksheet('Data')

          if (sessions.length > 0) {
            // Add headers based on first session object
            const headers = Object.keys(sessions[0])
            worksheet.addRow(headers)

            // Add data rows
            sessions.forEach((session) => {
              worksheet.addRow(Object.values(session))
            })
          }

          /* export to XLSX */
          const buffer = await workbook.xlsx.writeBuffer()
          const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `${filename}.xlsx`
          link.click()
          URL.revokeObjectURL(url)
        } catch (error) {
          console.error('Error exporting sessions', error)
          toast?.error(t('moreActions.admin.sessions.export.error'))
        } finally {
          moreActionsStore.setAttachedData('admin-sessions-export', { isLoading: false })
        }
      },
      label: 'moreActions.admin.sessions.XLSXExport',
      icon: 'download',
      restrictions: {
        programRoles: [RoleEnumTypeV2.ADMIN, RoleEnumTypeV2.MANAGER],
      },
    },
  ],
  attachedData: { isLoading: false },
} satisfies AdminSessionsExportMoreActionsType

const adminUsersImportActionItem = {
  name: 'admin-users-import',
  actions: [
    {
      fn: ({}) => {
        modalsStore.toggleModal('admin-users-import', true)
      },
      label: 'moreActions.admin.users.importFromExcel',
      icon: 'add-file',
      restrictions: {
        programRoles: [RoleEnumTypeV2.ADMIN],
      },
    },
    {
      fn: ({}) => {
        modalsStore.toggleModal('admin-manual-user-import', true)
      },
      label: 'moreActions.admin.users.emailInvitation',
      icon: 'email',
      restrictions: {
        programRoles: [RoleEnumTypeV2.ADMIN, RoleEnumTypeV2.MANAGER],
      },
    },
  ],
  attachedData: { isLoading: false },
} satisfies AdminUsersImportMoreActionsType

const userRowActionItem = {
  name: 'user-row',
  actions: [
    {
      fn: ({ attachedData }) => {
        const { user } = attachedData as UserRowMoreActionsType['attachedData']
        if (!user) return

        modalsStore.toggleModal('user-profile', true)
        modalsStore.updateModalAttachedData<UserProfileModalType>('user-profile', { userId: user.id! })
      },
      label: 'moreActions.user.sendMessage',
      icon: 'show',
      restrictions: {
        groupRoles: [RoleEnumTypeV2.MENTOR, RoleEnumTypeV2.MENTEE],
        custom: () => {
          return authStore.userActivePermissionGroup === PermissionsGroupsEnumType.USER
        },
      },
    },
    {
      fn: ({ attachedData }) => {
        const { user } = attachedData as UserRowMoreActionsType['attachedData']
        if (!user) return

        modalsStore.toggleModal('edit-profile', true)
        modalsStore.updateModalAttachedData<EditProfileModalType>('edit-profile', {
          userId: user.id!,
          email: user.email,
        })
      },
      label: 'moreActions.user.edit',
      icon: 'edit',
      restrictions: {
        programRoles: [RoleEnumTypeV2.ADMIN],
        custom: () => {
          return authStore.userActivePermissionGroup === PermissionsGroupsEnumType.ADMIN
        },
      },
    },
    {
      fn: async ({ attachedData }) => {
        const { user } = attachedData as UserRowMoreActionsType['attachedData']
        if (!user) return

        // Open the impersonation modal with reason prompt
        modalsStore.updateModalAttachedData('impersonation-reason', {
          targetUserId: user.id,
          targetUserEmail: user.email,
          targetUserRoles: user.roles || [],
        })
        modalsStore.toggleModal('impersonation-reason', true)
      },
      label: 'impersonation.action.start',
      icon: 'user',
      isDanger: false,
      restrictions: {
        custom: (attachedData, context) => {
          const { user } = attachedData as UserRowMoreActionsType['attachedData']
          if (!user) return false

          // User needs both ADMIN and SUPPORT roles
          const currentUserRoles = context?.programRoles || []
          return currentUserRoles.includes(RoleEnumTypeV2.ADMIN) && currentUserRoles.includes(RoleEnumTypeV2.SUPPORT)
        },
      },
    },
    {
      fn: async ({ attachedData, toast, t }) => {
        const { user, program } = attachedData as UserRowMoreActionsType['attachedData']
        try {
          if (!user || !program) throw new Error('User or program is undefined')
          const { sent, skippedSso } = await sendPasswords(program.id, [user.id])
          // KC-493 : un compte SSO ne reçoit rien, on ne dit plus « envoyé ».
          if (!sent && skippedSso) {
            toast.error(t('moreActions.user.sendInvitation.sso'))
            return
          }
          toast.success(t('moreActions.user.sendInvitation.success'))
        } catch (error) {
          toast.error(t('error.common'))
          console.error('Error sending invitation', error)
        }
      },
      label: 'moreActions.user.sendInvitation',
      icon: 'email',
      restrictions: {
        programRoles: [RoleEnumTypeV2.ADMIN],
      },
    },
    {
      // KC-635 : le fournisseur SSO de l'établissement refuse la personne
      // (groupe non autorisé, compte invité, adresse perso) : on la passe en
      // e-mail + mot de passe et on lui envoie l'invitation.
      fn: async ({ attachedData, toast, t }) => {
        const { user, program } = attachedData as UserRowMoreActionsType['attachedData']
        try {
          if (!user?.id || !program) throw new Error('User or program is undefined')
          const { invitationSent } = await switchUserToPasswordLoginMutation({
            userId: user.id,
            programId: program.id,
          })
          await invalidateAdminUsers(program.id)
          toast.success(
            t(
              invitationSent
                ? 'moreActions.user.switchToPasswordLogin.success'
                : 'moreActions.user.switchToPasswordLogin.successNoInvitation',
            ),
          )
        } catch (error: any) {
          const code = error?.response?.code
          if (code === 'ALREADY_PASSWORD_LOGIN') {
            toast.error(t('moreActions.user.switchToPasswordLogin.alreadyPassword'))
            return
          }
          if (code === 'EMAIL_LOGIN_DISABLED') {
            toast.error(t('moreActions.user.switchToPasswordLogin.emailLoginDisabled'))
            return
          }
          toast.error(t('error.common'))
          console.error('Error switching user to password login', error)
        }
      },
      label: 'moreActions.user.switchToPasswordLogin',
      icon: 'change',
      requiresConfirm: true,
      hint: () => 'moreActions.user.loginMethod.sso',
      confirmLabel: 'moreActions.user.switchToPasswordLogin.confirmTitle',
      confirmContent: 'moreActions.user.switchToPasswordLogin.confirmLabel',
      restrictions: {
        programRoles: [RoleEnumTypeV2.ADMIN],
        // Super admin seulement (ADMIN + SUPPORT, décision Danny 26/10), pour un
        // compte en SSO, et si la plateforme accepte le mot de passe.
        custom: (attachedData, context) => {
          const { user } = attachedData as UserRowMoreActionsType['attachedData']
          return (
            isSuperAdminContext(context) &&
            (user as V2UserAdminDataType | undefined)?.loginMethod === 'sso' &&
            getPlatformSetting('EMAIL_LOGIN_ENABLED') !== 'false'
          )
        },
      },
    },
    {
      // KC-635 : l'inverse. Le compte est rattaché au SSO de la plateforme sur
      // son adresse actuelle ; son mot de passe ne fonctionne plus.
      fn: async ({ attachedData, toast, t }) => {
        const { user, program } = attachedData as UserRowMoreActionsType['attachedData']
        try {
          if (!user?.id || !program) throw new Error('User or program is undefined')
          await switchUserToSsoLoginMutation({ userId: user.id, programId: program.id })
          await invalidateAdminUsers(program.id)
          toast.success(t('moreActions.user.switchToSsoLogin.success'))
        } catch (error: any) {
          const code = error?.response?.code
          if (code === 'ALREADY_SSO_LOGIN') {
            toast.error(t('moreActions.user.switchToSsoLogin.alreadySso'))
            return
          }
          if (code === 'SSO_DISABLED') {
            toast.error(t('moreActions.user.switchToSsoLogin.ssoDisabled'))
            return
          }
          if (code === 'SSO_IDENTITY_IN_USE') {
            toast.error(t('moreActions.user.switchToSsoLogin.identityInUse'))
            return
          }
          toast.error(t('error.common'))
          console.error('Error switching user to SSO login', error)
        }
      },
      label: 'moreActions.user.switchToSsoLogin',
      icon: 'change',
      requiresConfirm: true,
      hint: (attachedData) => {
        const { user } = attachedData as UserRowMoreActionsType['attachedData']
        return (user as V2UserAdminDataType | undefined)?.loginMethod === 'pending'
          ? 'moreActions.user.loginMethod.pending'
          : 'moreActions.user.loginMethod.password'
      },
      confirmLabel: 'moreActions.user.switchToSsoLogin.confirmTitle',
      confirmContent: 'moreActions.user.switchToSsoLogin.confirmLabel',
      restrictions: {
        programRoles: [RoleEnumTypeV2.ADMIN],
        // Super admin seulement, si la plateforme a le SSO, pour un compte qui n'y est pas.
        custom: (attachedData, context) => {
          const { user } = attachedData as UserRowMoreActionsType['attachedData']
          const loginMethod = (user as V2UserAdminDataType | undefined)?.loginMethod
          return (
            isSuperAdminContext(context) &&
            (loginMethod === 'password' || loginMethod === 'pending') &&
            getPlatformSetting('SAML_LOGIN_ENABLED') === 'true'
          )
        },
      },
    },
    {
      fn: async ({ attachedData, toast, t }) => {
        const { user, program } = attachedData as UserRowMoreActionsType['attachedData']
        try {
          if (!user || !program) {
            throw new Error('User or program is undefined')
          }
          await deleteUserFromProgram(user.id, program.id)
          await invalidateAdminUsers(program.id)
          toast.success(t('moreActions.user.deleteFromProgram.success'))
        } catch (error) {
          toast.error(t('error.common'))
          console.error('Error deleting user', error)
        }
      },
      label: 'moreActions.user.deleteFromProgram',
      icon: 'cancel',
      isDanger: true,
      confirmLabel: 'moreActions.user.deleteFromProgram.confirmLabel',
      restrictions: {
        programRoles: [RoleEnumTypeV2.ADMIN],
        // Un super admin ne se retire que depuis « Ma plateforme ».
        custom: (attachedData) => {
          const { user } = attachedData as UserRowMoreActionsType['attachedData']
          return !isSuperAdminContext({ programRoles: user?.roles })
        },
      },
    },
  ],
  attachedData: {},
} satisfies UserRowMoreActionsType

const adminGroupActionItem = {
  name: 'admin-group',
  actions: [
    {
      fn: ({ attachedData }) => {
        const group = attachedData as AdminGroupActionsType['attachedData']

        modalsStore.toggleModal('admin-group-structure', true)
        modalsStore.updateModalAttachedData<AdminGroupStructureModalType>('admin-group-structure', {
          groupId: group.id!,
        })
      },
      label: 'moreActions.admin.groups.modifyStructure',
      icon: 'edit',
      restrictions: {
        programRoles: [RoleEnumTypeV2.ADMIN, RoleEnumTypeV2.MANAGER],
      },
    },
    {
      fn: ({ attachedData }) => {
        const group = attachedData as AdminGroupActionsType['attachedData']

        modalsStore.toggleModal('group-settings', true)
        modalsStore.updateModalAttachedData<GroupSettingsModalType>('group-settings', {
          groupId: group.id!,
        })
      },
      label: 'moreActions.admin.groups.modifySettings',
      icon: 'edit',
      restrictions: {
        programRoles: [RoleEnumTypeV2.ADMIN, RoleEnumTypeV2.MANAGER],
      },
    },
  ],
  attachedData: {},
} satisfies AdminGroupActionsType

export type MoreActionsStoreValuesType = {
  moreActions: Record<MoreActionsStoreType['name'], MoreActionsStoreType>
}

export const INITIAL_MORE_ACTIONS_STORE_VALUES: MoreActionsStoreValuesType = {
  moreActions: {
    'account': accountActionItem,
    'session-card': sessionCardActionItem,
    'user-card': userCardActionItem,
    'user-row': userRowActionItem,
    'admin-sessions-export': adminSessionExportActionItem,
    'admin-users-import': adminUsersImportActionItem,
    'admin-group': adminGroupActionItem,
  },
}

const moreActionsStore = subscribeToStore(
  create(
    combine(INITIAL_MORE_ACTIONS_STORE_VALUES, (set, get) => ({
      reset: () => resetMoreActionsStoreAction(set),
      setAttachedData: (name: MoreActionsStoreType['name'], attachedData: MoreActionsStoreType['attachedData']) =>
        setAttachedDataAction(get, set, name, attachedData),
    })),
  ),
)
export default moreActionsStore
